import apiClient from "@/api/apiClient";
import { isGuestUser, getToken } from "./storage";

/**
 * Check if user has access to tasks based on subscription status
 * Returns { hasAccess: boolean, reason: string }
 *
 * Guest users always have access.
 * Unauthenticated users (no token, no guest) are allowed access (for starting new guest exams).
 * Paid users are blocked if subscription status is "canceled".
 *
 * Deliberate Trial bypass is caller-scoped via `options.deliberateTrial`. This
 * function never reads sessionStorage, so a stale `exam_mode` value alone can
 * never grant subscription access to an Individual/paid flow.
 */
export async function checkSubscriptionAccess(options?: {
    deliberateTrial?: boolean;
}): Promise<{
    hasAccess: boolean;
    reason: string;
}> {
    try {
        if (options?.deliberateTrial) {
            console.log("✅ Deliberate Trial (explicit caller) - access granted without subscription check");
            return { hasAccess: true, reason: "deliberate_trial" };
        }

        // Guest users always have access
        if (isGuestUser()) {
            console.log("✅ Guest user - access granted");
            return { hasAccess: true, reason: "guest_user" };
        }

        // Check if user has authentication token
        const token = getToken();
        if (!token) {
            console.log("ℹ️ No authentication token - allowing access (unauthenticated/guest flow)");
            return { hasAccess: true, reason: "no_token" };
        }

        // Check subscription status for authenticated paid users
        console.log("🔍 Checking subscription status for authenticated user...");
        const response = await apiClient.post("/get_user_subscriptions", {});
        const data = response.data;

        if (!data.subscriptions || data.subscriptions.length === 0) {
            console.log("⚠️ No subscription found - blocking access");
            return { hasAccess: false, reason: "no_subscription" };
        }

        // Find active or scheduled subscription
        const activeSub = data.subscriptions.find(
            (s: any) =>
                s.status &&
                (s.status.toLowerCase() === "active" ||
                    s.status.toLowerCase() === "cancel_scheduled" ||
                    s.status.toLowerCase() === "trialing")
        );

        if (activeSub) {
            console.log("✅ Active subscription found - access granted");
            return { hasAccess: true, reason: "active_subscription" };
        }

        // Check if all subscriptions are canceled
        const allCanceled = data.subscriptions.every(
            (s: any) => s.status && s.status.toLowerCase() === "canceled"
        );

        if (allCanceled) {
            console.log("❌ All subscriptions are canceled - blocking access");
            return { hasAccess: false, reason: "subscription_canceled" };
        }

        // Default to blocking if status is unclear
        console.log("⚠️ Unclear subscription status - blocking access");
        return { hasAccess: false, reason: "unclear_status" };
    } catch (error) {
        console.error("❌ Error checking subscription access:", error);
        // On error, allow access to avoid blocking users due to API issues
        // This is a safety measure - you may want to change this behavior
        return { hasAccess: true, reason: "error_occurred" };
    }
}
